import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  MessageSquare,
  Heart,
  TrendingUp,
  Brain,
  Zap,
  BookOpen,
  Award,
  Briefcase,
  GraduationCap,
  CheckCircle2
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

export const ProfessionalDevelopment = () => {
  const navigate = useNavigate();

  const pageData = {
    h1: 'Professional Development Training Programs',
    description: 'Professional Development Training Programs are designed to enhance employees\u2019 skills, knowledge, and performance to meet evolving industry demands. These programs focus on building leadership capabilities, technical expertise, communication skills, and workplace efficiency, enabling professionals to grow confidently in their careers. By investing in structured professional development training, organizations improve productivity, employee engagement, and long-term business success while staying competitive in today\u2019s dynamic work environment.',
    meta_title: 'Professional Development Training Programs | Leadership, Communication & Skills Courses | ISDE Academy',
    meta_description: 'Professional Development Training Programs in Chennai including Soft Skills Training, Leadership Development, Management Skills, and Business Communication. Customized programs for professionals and corporations.',
    meta_keywords: 'professional development training programs, professional development training, leadership training courses, soft skills training, communication skills, emotional intelligence, management development, corporate training chennai, business communication, skill gap reduction, employee performance, leadership pipeline',
    canonicalUrl: 'https://isde.academy/training/professional-development-training-programs'
  };

  const keyReasons = [
    'Skill gap reduction across industries',
    'Improved employee performance and productivity',
    'Leadership pipeline development',
    'Adaptation to digital transformation',
    'Enhanced employability and career mobility'
  ];

  const courseCategories = [
    {
      id: 'soft-skills',
      title: 'Soft Skills Training',
      duration: '1-5 Days',
      icon: Heart,
      color: 'text-rose-600',
      bgColor: 'bg-rose-50',
      description: 'Comprehensive soft skills development covering communication, leadership, emotional intelligence, teamwork, and professional etiquette for workplace excellence.',
      isClickable: true,
      link: '/training/soft-skills-training-chennai',
      courses: [
        {
          name: 'Communication Skills Training',
          duration: '2-3 Days',
          level: 'All Levels',
          description: 'Master verbal, non-verbal, and written communication including presentations, business writing, and active listening.'
        },
        {
          name: 'Leadership & Management Skills',
          duration: '3 Days',
          level: 'Managers & Aspiring Leaders',
          description: 'Develop leadership presence, decision-making, delegation, and team motivation capabilities.'
        },
        {
          name: 'Emotional Intelligence (EQ)',
          duration: '2 Days',
          level: 'All Professionals',
          description: 'Build self-awareness, empathy, stress management, and resilience for workplace success.'
        },
        {
          name: 'Interpersonal & Teamwork Skills',
          duration: '2 Days',
          level: 'All Levels',
          description: 'Enhance collaboration, conflict resolution, trust building, and cross-functional teamwork.'
        },
        {
          name: 'Professional Etiquette & Workplace Behavior',
          duration: '1-2 Days',
          level: 'Fresh Graduates & Professionals',
          description: 'Master corporate etiquette, business conduct, cultural sensitivity, and professional networking.'
        }
      ],
      benefits: [
        'Improved workplace communication and confidence',
        'Enhanced leadership and team collaboration',
        'Better emotional intelligence and stress management',
        'Increased employability and career growth',
        'Stronger client relationships and professional presence'
      ]
    },
    {
      id: 'leadership-management',
      title: 'Leadership & Management Development',
      duration: '2-5 Days',
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      description: 'Develop essential leadership and management skills for the modern Indian workplace with proven frameworks and practical techniques.',
      isClickable: true,
      link: '/training/leadership-and-management-training',
      courses: [
        {
          name: 'Transformational Leadership Program',
          duration: '3 Days',
          level: 'Mid to Senior Management',
          description: 'Advanced leadership program focusing on transforming yourself and your team for organizational excellence.'
        },
        {
          name: 'First-Time Manager Bootcamp',
          duration: '2 Days',
          level: 'New Managers',
          description: 'Essential skills for transitioning from individual contributor to effective manager in Indian corporate culture.'
        },
        {
          name: 'Strategic Thinking for Leaders',
          duration: '2 Days',
          level: 'Senior Management',
          description: 'Develop strategic mindset and decision-making capabilities for long-term organizational success.'
        },
        {
          name: 'Team Building & Motivation',
          duration: '1 Day',
          level: 'All Managers',
          description: 'Create high-performing teams through effective motivation techniques and team dynamics understanding.'
        }
      ],
      benefits: [
        'Enhanced decision-making and problem-solving abilities',
        'Improved team productivity and engagement',
        'Better conflict resolution and change management skills',
        'Increased organizational impact and career advancement',
        'Cultural intelligence for diverse Indian workforce'
      ]
    },
    {
      id: 'communication-skills',
      title: 'Business Communication & Presentation Skills',
      duration: '1-3 Days',
      icon: MessageSquare,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      description: 'Master professional communication, presentation, and interpersonal skills critical for success in the Indian business environment.',
      isClickable: true,
      link: '/training/communication-skills-training-chennai',
      courses: [
        {
          name: 'Effective Business Communication',
          duration: '2 Days',
          level: 'All Levels',
          description: 'Improve written and verbal communication skills for emails, reports, and professional interactions.'
        },
        {
          name: 'Presentation Skills Mastery',
          duration: '2 Days',
          level: 'All Levels',
          description: 'Deliver impactful presentations with confidence, clarity, and persuasion in corporate settings.'
        },
        {
          name: 'Emotional Intelligence at Work',
          duration: '2 Days',
          level: 'All Levels',
          description: 'Develop self-awareness, empathy, and social skills for better workplace relationships and leadership.'
        },
        {
          name: 'Negotiation & Influence Skills',
          duration: '2 Days',
          level: 'Mid to Senior Level',
          description: 'Master negotiation tactics and influencing strategies for business deals and stakeholder management.'
        }
      ],
      benefits: [
        'Clear and confident communication in professional settings',
        'Enhanced presentation and public speaking abilities',
        'Improved interpersonal relationships and emotional intelligence',
        'Better negotiation outcomes and stakeholder buy-in',
        'Cross-cultural communication effectiveness'
      ]
    }
  ];


  const structuredData = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "ISDE Academy",
    "description": pageData.description,
    "url": "https://isde.academy",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Chennai",
      "addressRegion": "Tamil Nadu",
      "addressCountry": "India"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Professional Development Training Courses",
      "itemListElement": courseCategories.map(cat => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Course",
          "name": cat.title,
          "description": cat.description,
          "provider": {
            "@type": "Organization",
            "name": "ISDE Academy",
            "url": "https://isde.academy"
          }
        }
      }))
    }
  };

  return (
    <>
      <Helmet>
        <title>{pageData.meta_title}</title>
        <meta name="description" content={pageData.meta_description} />
        <meta name="keywords" content={pageData.meta_keywords} />
        <link rel="canonical" href={pageData.canonicalUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageData.meta_title} />
        <meta property="og:description" content={pageData.meta_description} />
        <meta property="og:url" content={pageData.canonicalUrl} />
        <meta property="og:site_name" content="ISDE Academy" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageData.meta_title} />
        <meta name="twitter:description" content={pageData.meta_description} />

        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 text-white py-24 mt-20 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 -left-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob"></div>
            <div className="absolute top-0 -right-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob animation-delay-2000"></div>
            <div className="absolute -bottom-8 left-20 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob animation-delay-4000"></div>
          </div>

          <div className="absolute top-10 right-10 opacity-20">
            <TrendingUp className="h-32 w-32" />
          </div>
          <div className="absolute bottom-10 left-10 opacity-20">
            <Brain className="h-24 w-24" />
          </div>

          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <motion.div
                  className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <Zap className="h-5 w-5 text-yellow-200" />
                  <span className="text-sm font-semibold text-emerald-100">Skills for the Future Workplace</span>
                </motion.div>

                <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                  {pageData.h1}
                </h1>
                <p className="text-xl text-emerald-100 mb-8 leading-relaxed">
                  {pageData.description}
                </p>

                <div className="flex flex-wrap gap-4">
                  <Button
                    onClick={() => navigate('/contact')}
                    className="bg-white text-emerald-600 hover:bg-emerald-50 font-semibold px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all"
                  >
                    Get Started
                  </Button>
                  <Button
                    onClick={() => navigate('/training-calendar')}
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-emerald-600 font-semibold px-8 py-6 text-lg"
                  >
                    View Schedule
                  </Button>
                </div>
              </motion.div>

              <motion.div
                className="grid grid-cols-2 gap-4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <motion.div
                  className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
                  whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.15)" }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="text-4xl font-bold mb-2">3</div>
                  <div className="text-emerald-100 text-sm">Training Categories</div>
                </motion.div>

                <motion.div
                  className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
                  whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.15)" }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="text-4xl font-bold mb-2">20+</div>
                  <div className="text-emerald-100 text-sm">Expert Courses</div>
                </motion.div>

                <motion.div
                  className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
                  whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.15)" }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="text-4xl font-bold mb-2">100%</div>
                  <div className="text-emerald-100 text-sm">Practical & Interactive</div>
                </motion.div>

                <motion.div
                  className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
                  whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.15)" }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="text-4xl font-bold mb-2">3000+</div>
                  <div className="text-emerald-100 text-sm">Professionals Upskilled</div>
                </motion.div>
              </motion.div>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F9FAFB"/>
            </svg>
          </div>
        </section>

        {/* Key Reasons Section - h2 */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-3xl font-bold mb-6 text-center text-gray-800">
              Key Reasons Professional Development Training Is Critical
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {keyReasons.map((reason, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-3 bg-white rounded-xl p-4 shadow-sm border border-gray-100"
                >
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 font-medium text-sm">{reason}</span>
                </motion.div>
              ))}
            </div>
            <p className="text-center text-gray-600 leading-relaxed max-w-3xl mx-auto">
              According to industry reports, organizations that invest in ongoing professional development see higher employee retention, better engagement, and improved business outcomes.
            </p>
          </motion.div>
        </section>

        {/* Course Categories Grid */}
        <section className="max-w-7xl mx-auto px-4 py-12">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-800">Our Professional Development Programs</h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courseCategories.map((category, index) => {
              const IconComponent = category.icon;
              const isClickable = category.isClickable && category.link;

              return (
                <motion.article
                  key={category.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card
                    className={`h-full overflow-hidden hover:shadow-xl transition-all hover:-translate-y-2 group ${isClickable ? 'cursor-pointer' : ''}`}
                    onClick={isClickable ? () => navigate(category.link) : undefined}
                  >
                    <div className={`bg-gradient-to-br ${category.bgColor} bg-opacity-10 p-6 border-b-4 ${category.color.replace('text-', 'border-')}`}>
                      <div className={`${category.bgColor} w-16 h-16 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                        <IconComponent className={`h-8 w-8 ${category.color}`} />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        {category.title}
                        {isClickable && (
                          <span className="ml-2 text-sm font-normal text-blue-600">→</span>
                        )}
                      </h3>
                      <p className={`${category.color} font-semibold text-sm mb-3`}>Duration: {category.duration}</p>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {category.description}
                      </p>
                    </div>

                    <CardContent className="p-6 bg-white">
                      <h4 className="font-bold text-sm text-gray-900 mb-3 uppercase tracking-wide">
                        {isClickable ? 'Featured Courses:' : 'Available Courses:'}
                      </h4>
                      <ul className="space-y-2 mb-4">
                        {category.courses.slice(0, isClickable ? 5 : 4).map((course, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <BookOpen className={`h-4 w-4 ${category.color} flex-shrink-0 mt-0.5`} />
                            <div className="flex-1">
                              <span className="text-gray-700 text-sm font-medium">{course.name}</span>
                              <div className="flex gap-2 mt-0.5">
                                <span className={`text-xs ${category.color}`}>{course.duration}</span>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>

                      <div className={`${category.bgColor} p-3 rounded-lg mt-4`}>
                        <p className={`text-xs font-semibold ${category.color} uppercase tracking-wide mb-1`}>Key Benefits:</p>
                        <p className="text-gray-700 text-sm">{category.benefits[0]}</p>
                      </div>

                      {!isClickable && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/training-calendar');
                            }}
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm"
                            size="sm"
                          >
                            View Schedule
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.article>
              );
            })}
          </div>
        </section>

        {/* Why Choose Us Section - h3 */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h3 className="text-3xl font-bold mb-12 text-center text-gray-800">Why Choose Us for Your Professional Development Training Programs?</h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-emerald-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-emerald-600" />
                </div>
                <h4 className="text-xl font-bold mb-2">Industry Expertise</h4>
                <p className="text-gray-600">10+ years of experience training professionals across manufacturing, IT, and service sectors in India.</p>
              </div>
              <div className="text-center">
                <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-blue-600" />
                </div>
                <h4 className="text-xl font-bold mb-2">Practical Learning</h4>
                <p className="text-gray-600">Interactive workshops with real-world case studies and hands-on exercises from Indian business context.</p>
              </div>
              <div className="text-center">
                <div className="bg-purple-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Briefcase className="h-8 w-8 text-purple-600" />
                </div>
                <h4 className="text-xl font-bold mb-2">Career Impact</h4>
                <p className="text-gray-600">Certification and skills that lead to measurable career growth and organizational advancement.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Upskill Your Team?
            </h2>
            <p className="text-xl text-emerald-100 mb-8">
              Join thousands of professionals who have advanced their careers with our training programs
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/contact')}
                className="bg-white text-emerald-600 hover:bg-emerald-50 font-semibold px-8 py-3 text-lg"
              >
                Request Corporate Training
              </Button>
              <Button
                onClick={() => navigate('/training-calendar')}
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-emerald-600 font-semibold px-8 py-3 text-lg"
              >
                View Schedule
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};
